/** A professional price-range entry (from `professionalOverview.PriceRanges`). */
export interface ProfessionalPriceRange {
    Title: string;
    SortOrder: number;
}

/** Splits a price-range title into its bounds. A title can itself be a range
 *  ("$250k - $500k"), so the span is built from the lowest bound of the first
 *  entry and the highest bound of the last — never the doubled-up
 *  "$250k - $500k – $500k - $1m". A single value yields equal bounds. */
const RANGE_SEPARATOR = /\s*[-–—]\s*|\s+to\s+/i;

const splitRangeBounds = (title: string): { low: string; high: string } => {
    const trimmed = title.trim();
    const parts = title
        .split(RANGE_SEPARATOR)
        .map((part) => part.trim())
        .filter(Boolean);
    return {
        low: parts[0] ?? trimmed,
        high: parts[parts.length - 1] ?? trimmed,
    };
};

/** A bound that opens at zero ("$0", "Under $500k") — the span should read as
 *  "Up to {high}" rather than a literal "$0 – …" that implies free work. */
const isZeroBound = (bound: string): boolean => {
    if (/^(under|below|up to|less than)\b/i.test(bound.trim())) return true;
    const numeric = bound.replace(/[^0-9.]/g, '');
    return numeric !== '' && Number.parseFloat(numeric) === 0;
};

/** An open-ended top bound ("$5m+", "$5m or more") — the span should read as
 *  "From {low}" rather than pinning a fabricated ceiling. */
const isOpenEnded = (bound: string): boolean =>
    /\+|plus|over|more/i.test(bound);

/**
 * The "typical project value" label shown in the detail-tab intros — the
 * sorted price-range span, e.g. "$5m – $10m", or a single range when the lowest
 * and highest are the same. Each range Title may itself be a span, so the lowest
 * bound of the first entry and the highest bound of the last are used.
 *
 * The edges are reframed so they read naturally: a zero floor becomes
 * "Up to {high}", an open-ended top becomes "From {low}", and a span that runs
 * from zero to open-ended (any budget) is dropped as uninformative. Returns
 * undefined when no ranges are set so callers hide the stat rather than show a
 * fabricated figure.
 */
export const formatProjectValueRange = (
    priceRanges: ReadonlyArray<ProfessionalPriceRange> | null | undefined,
): string | undefined => {
    const sorted = (priceRanges ?? [])
        .slice()
        .sort((a, b) => a.SortOrder - b.SortOrder);
    const first = sorted[0]?.Title;
    const last = sorted[sorted.length - 1]?.Title;

    if (!first || !last) return undefined;
    const low = splitRangeBounds(first).low;
    const high = splitRangeBounds(last).high;
    if (low === high) return low;

    const zeroLow = isZeroBound(low);
    const openHigh = isOpenEnded(high);
    if (zeroLow && openHigh) return undefined;
    if (zeroLow) return `Up to ${high}`;
    if (openHigh) return `From ${low}`;
    return `${low} – ${high}`;
};
